import enquire from "enquire.js";
import debounce from "debounce";
import { Fancyapps } from "@Libraries/Fancyapps";
import { Fancybox } from "typings/Fancyapps";
import floatSidebar from 'float-sidebar';
import { Media } from "@Utilities/Media";
import Offcanvas from "bootstrap/js/dist/offcanvas";
import { ReadMore } from "@Utilities/ReadMore";

export class BuildGroupPage
{
	private readonly _categoryContentSelector = "#catContent";
	private readonly _clickEventName = "click";
	private readonly _filterContentSelector = "#filterContent";
	private _floatingSidebar: floatSidebar;
	private readonly _messageEventName = "message";
	private readonly _productIdAttributeName = "data-product-id";
	private _quickViewLinks: NodeListOf<HTMLElement>;
	private _quickViewModal: Fancybox;
	private readonly _quickViewSelector = ".quickView";
	private readonly _quickViewModalClass = "quickShopModal";
	private readonly _readClampSelector = ".readClamp";
	private readonly _resizeEventName = "resize";
	private readonly _sidebarSelector = "#desktopFilter #filter";
	private readonly _styleAttributeName = "style";

	public DesktopFilterEl: HTMLElement;
	public FilterEl: HTMLElement;
	public MobileFilterEl: HTMLElement;
	public MobileFilterContainer: HTMLElement;
	public MobileFilterInstance: Offcanvas;

	constructor()
	{
		this.Initialize();
	}

	public ConfigureFilter = (): void =>
	{
		this.FilterEl = document.getElementById("filter");
		this.DesktopFilterEl = document.getElementById("desktopFilter");
		this.MobileFilterEl = document.getElementById("mobileFilter");
		this.MobileFilterContainer = document.getElementById("offcanvasFilter");
		this.MobileFilterInstance = Offcanvas.getOrCreateInstance(this.MobileFilterContainer);
	};

	public ConfigureQuickViewLinks(): void
	{
		const messageHandler = this.QuickViewClose.bind(this);
		const openHandler = this.QuickViewHandler.bind(this);

		window.addEventListener(this._messageEventName, messageHandler);

		this._quickViewLinks.forEach((link: HTMLElement) =>
		{
			link.addEventListener(this._clickEventName, openHandler);
		});
	};

	public ConfigureReadMore = (): void =>
	{
		const readClamp: NodeListOf<HTMLElement> = document.querySelectorAll(this._readClampSelector);

		if (readClamp.length > 0)
		{
			readClamp.forEach(clamp =>
			{
				new ReadMore(clamp);
			});
		}
	};

	public ConfigureSidebar(): void
	{
		const resizeHandler = debounce(() =>
		{
			this._floatingSidebar.forceUpdate();
		}, 200);

		enquire.register(Media.MediaDesktop, {
			match: (): void =>
			{
				document.querySelector(this._filterContentSelector).setAttribute(this._styleAttributeName, "");
				this.EnableStickySidebar();
				window.addEventListener(this._resizeEventName, resizeHandler);
			},
			unmatch: (): void =>
			{
				this._floatingSidebar.destroy();
				document.querySelector(this._filterContentSelector).setAttribute(this._styleAttributeName, "");
				window.removeEventListener(this._resizeEventName, resizeHandler);
			}
		});
	};

	public EnableStickySidebar(): void
	{
		const content = document.querySelector(this._categoryContentSelector);
		const sidebar = document.querySelector(this._sidebarSelector);

		this._floatingSidebar = floatSidebar({
			sidebar: sidebar,
			relative: content,
			topSpacing: 20,
			bottomSpacing: 80
		});
	};

	public async Initialize(): Promise<void>
	{
		this.ConfigureFilter();
		this.ConfigureReadMore();

		enquire.register(Media.MediaMobile, {
			deferSetup: true,
			match: () =>
			{
				this.MobileFilterSetup();
			},
			unmatch: () =>
			{
				this.MobileFilterDestroy();
			}
		});

		this._quickViewLinks = document.querySelectorAll(this._quickViewSelector);

		if (document.querySelector(this._filterContentSelector) !== null)
		{
			this.ConfigureSidebar();
		}

		if (this._quickViewLinks.length > 0)
		{
			this.ConfigureQuickViewLinks();
		}
	}

	public MobileFilterDestroy = (): void =>
	{
		this.DesktopFilterEl.innerHTML = this.FilterEl.outerHTML;
		this.MobileFilterEl.innerHTML = "";
		this.MobileFilterInstance.hide();
	};

	public MobileFilterSetup = (): void =>
	{
		this.MobileFilterEl.innerHTML = this.FilterEl.outerHTML;
		this.DesktopFilterEl.innerHTML = "";
	};

	public async QuickViewClose(e: MessageEvent): Promise<void>
	{
		if (e.data === "quickViewClose")
		{
			this._quickViewModal.close();
		}
	}

	public async QuickViewHandler(e: Event): Promise<void>
	{
		e.preventDefault();

		if (e.target instanceof Element)
		{
			const id = e.target.closest(".ps-item").parentElement.getAttribute(this._productIdAttributeName);
			const href = `/product/quickview/${id}`;
			const fancyapps = await Fancyapps.Import();
			const fancybox = fancyapps.Fancybox;

			this._quickViewModal = new fancybox([
				{
					src: href,
					type: "iframe"
				}
			], {
				mainClass: this._quickViewModalClass
			});
		}
	};
}